/**
 * Google Maps JS API loader (single-load, cached).
 *
 * This prevents dozens of components from duplicating script injection logic.
 * API key is fetched from backend.
 */
import { apiClient } from "../../api/httpClient";

const DEFAULT_LIBRARIES = ["places"];
const GOOGLE_API_KEY_ENDPOINT = "/config/google-api-key/";

async function fetchApiKeyFromServer() {
  if (typeof window === "undefined") return undefined;

  // Cache the in-flight request across the entire app.
  if (window.__googleMapsApiKeyPromise) return window.__googleMapsApiKeyPromise;

  window.__googleMapsApiKeyPromise = (async () => {
    const data = await apiClient(GOOGLE_API_KEY_ENDPOINT, "GET", {}, {}, { Accept: "application/json" });
    return data?.google_api_key;
  })()
    .catch((err) => {
      // Allow retry on later calls.
      window.__googleMapsApiKeyPromise = undefined;
      throw err;
    });

  return window.__googleMapsApiKeyPromise;
}

async function getApiKey() {
  const serverKey = await fetchApiKeyFromServer();
  if (serverKey) return serverKey;

  throw new Error("Google Maps API key is missing (server returned null/empty).");
}

function hasGooglePlaces() {
  return Boolean(window.google && window.google.maps && window.google.maps.places);
}

function removeLoaderScript(script) {
  script?.remove();
}

/**
 * Loads Google Maps JS API once and resolves when `window.google.maps.places` is ready.
 * @param {{ libraries?: string[] }} [options]
 * @returns {Promise<typeof window.google>}
 */
export function loadGoogleMaps(options = {}) {
  if (typeof window === "undefined") return Promise.resolve(undefined);

  if (hasGooglePlaces()) return Promise.resolve(window.google);

  // Reuse a single in-flight promise across the entire app.
  if (window.__googleMapsLoaderPromise) return window.__googleMapsLoaderPromise;

  window.__googleMapsLoaderPromise = new Promise((resolve, reject) => {
    (async () => {
      const existingScript = document.querySelector("script[data-google-maps-loader='true']");
      if (existingScript) {
        if (hasGooglePlaces()) return resolve(window.google);
        existingScript.addEventListener("load", () => {
          if (hasGooglePlaces()) resolve(window.google);
          else {
            removeLoaderScript(existingScript);
            reject(new Error("Google Maps script loaded but API was not available."));
          }
        });
        existingScript.addEventListener("error", () => {
          removeLoaderScript(existingScript);
          reject(new Error("Failed to load the Google Maps script."));
        });
        return;
      }

      const script = document.createElement("script");
      script.setAttribute("data-google-maps-loader", "true");

      const apiKey = await getApiKey();
      const libs = (options.libraries && options.libraries.length ? options.libraries : DEFAULT_LIBRARIES).join(",");

      const url = new URL("https://maps.googleapis.com/maps/api/js");
      url.searchParams.set("key", apiKey);
      if (libs) url.searchParams.set("libraries", libs);

      script.src = url.toString();
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        removeLoaderScript(script);
        reject(new Error("Failed to load the Google Maps script."));
      };
      script.onload = () => {
        if (hasGooglePlaces()) resolve(window.google);
        else {
          removeLoaderScript(script);
          reject(new Error("Google Maps script loaded but API was not available."));
        }
      };

      document
        .querySelectorAll("script[data-google-maps-loader='true']")
        .forEach(removeLoaderScript);
      document.head.appendChild(script);
    })().catch(reject);
  });

  // Allow retry if the loader fails (network, invalid key, etc.).
  window.__googleMapsLoaderPromise.catch(() => {
    window.__googleMapsLoaderPromise = undefined;
  });

  return window.__googleMapsLoaderPromise;
}

